import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

import { ThreadComposer } from "./ThreadComposer";
import { ThreadPost } from "./ThreadPost";
import { useAuth } from "../../components/contexts/AuthContext";
import { ClientReadOnlyScope } from "../../components/ClientReadOnlyScope";
import { isClientReadOnly } from "../../utils/clientAccess";
import apiClient, { sendMessageWithAttachments } from "../../api-client";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { MESSAGE_PAGE_SIZE } from "./SearchMessagesModal";
import { EditTaskModal } from "../TechnicalSetup/EditTaskModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { ArchiveEventMessagesModal } from "./ArchiveEventMessagesModal";
import { INTERNAL_THREADS_ENDPOINT, buildInternalThreadUrl } from "./threadUrlUtils";
import { CustomTextField } from "../../components/CustomTextField";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { DOCKED_HEADER_HEIGHT } from "../../components/Sidebar";

export function ThreadInterface({
  selectedClientId,
  mainThreadId,
  messageThreadId = null,
  messageThreadTitle = "",
  currentUser,
  clientData,
  participants = [],
  conversationsRefreshKey,
  initialPage = 1,
  highlightMessageId = null,
  highlightMessagePosition = null,
  onHighlightConsumed,
  onShowNotification = null,
  onMessageSent = null,
  height = null,
  // Hilos internos de agencia: sin cliente asociado
  internal = false,
}) {
  const theme = useTheme();
  const { interceptorReady, user } = useAuth();
  const isFreePlanReadOnly =
    !internal &&
    user?.role !== "client" &&
    isClientReadOnly(user, clientData ?? selectedClientId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // En móvil un único scroll: el del documento
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const containerHeight = { xs: "auto", md: height || "calc(100vh - 165px)" };
  const containerMinHeight = { xs: "calc(100dvh - 200px)", md: 0 };
  const [allMessages, setAllMessages] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadedPages, setLoadedPages] = useState([]);
  const [pageNavigation, setPageNavigation] = useState({});
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [pendingHighlightId, setPendingHighlightId] = useState(
    highlightMessageId ? String(highlightMessageId) : null
  );
  const [messagesRefreshKey, setMessagesRefreshKey] = useState(0);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalMessage, setTaskModalMessage] = useState(null);
  const [taskModalTask, setTaskModalTask] = useState(null);
  const [taskModalConsultants, setTaskModalConsultants] = useState([]);
  const [moveToNewThreadModalOpen, setMoveToNewThreadModalOpen] =
    useState(false);
  const [moveToNewThreadMessageId, setMoveToNewThreadMessageId] =
    useState(null);
  const [isMovingToNewThread, setIsMovingToNewThread] = useState(false);
  const [moveToNewThreadError, setMoveToNewThreadError] = useState("");
  const [moveToNewThreadTitle, setMoveToNewThreadTitle] = useState("");
  const [isLoadingMessageThread, setIsLoadingMessageThread] = useState(false);
  const [archiveEventModalOpen, setArchiveEventModalOpen] = useState(false);
  const [archiveEventTargetMessage, setArchiveEventTargetMessage] =
    useState(null);
  const [archiveEventRecommendations, setArchiveEventRecommendations] =
    useState([]);
  const [selectedArchiveEventIds, setSelectedArchiveEventIds] = useState([]);
  const [isArchivingEventMessages, setIsArchivingEventMessages] =
    useState(false);
  const isMessageThreadView = Boolean(messageThreadId);
  const messageThreadLabel = (messageThreadTitle || "").trim();

  const availableConsultants = useMemo(() => {
    return (clientData?.participants || []).filter((participant) =>
      ["consultant", "agency_admin", "superadmin"].includes(
        participant.role
      )
    );
  }, [clientData]);

  const messageRefs = useRef(new Map());
  const scrollContainerRef = useRef(null);
  const loadingPagesRef = useRef(new Set());
  const loadedPagesRef = useRef([]);
  const pageMessagesRef = useRef(new Map());
  const pageNavigationRef = useRef({});
  const activeThreadRef = useRef(mainThreadId);
  const activeMessageThreadRequestRef = useRef(null);
  const archiveEventRequestRef = useRef(0);

  const mergeOrderedMessageGroups = useCallback((...messageGroups) => {
    const orderedIds = [];
    const messagesById = new Map();

    messageGroups.flat().forEach((message) => {
      if (!message?.id) return;
      const messageId = String(message.id);
      if (!messagesById.has(messageId)) {
        orderedIds.push(messageId);
      }
      messagesById.set(messageId, message);
    });

    return orderedIds
      .map((messageId) => messagesById.get(messageId))
      .filter(Boolean);
  }, []);

  const resetPagingState = useCallback(({ clearRefs = true } = {}) => {
    setAllMessages([]);
    if (clearRefs) {
      messageRefs.current.clear();
    }
    setHighlightedMessageId(null);
    setPendingHighlightId(null);
    setLoadedPages([]);
    loadedPagesRef.current = [];
    setPageNavigation({});
    pageNavigationRef.current = {};
    loadingPagesRef.current.clear();
    pageMessagesRef.current.clear();
    setLoadingPage(false);
  }, []);

  const extractPageNumber = (url) => {
    if (!url) return null;
    if (typeof url === "number") {
      return Number.isFinite(url) ? Number(url) : null;
    }

    const stringUrl = String(url);

    try {
      const base =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "https://placeholder";
      const parsed = new URL(stringUrl, base);
      const pageParam = parsed.searchParams.get("page");
      if (pageParam) {
        return Number(pageParam);
      }

      // Django suele omitir ?page=1 y devolver la URL base.
      return 1;
    } catch (error) {
      const match = stringUrl.match(/page=(\d+)/);
      if (match) {
        return Number(match[1]);
      }

      return 1;
    }
  };

  const loadPage = useCallback(
    async (page, { replace = false, pageSize = MESSAGE_PAGE_SIZE } = {}) => {
      if (!mainThreadId || !interceptorReady || !page || page < 1) return null;

      const threadId = mainThreadId;
      const resolvedPageSize = Math.max(1, Number(pageSize) || MESSAGE_PAGE_SIZE);

      if (loadingPagesRef.current.has(page)) {
        return pageNavigationRef.current[page] || null;
      }

      loadingPagesRef.current.add(page);
      setLoadingPage(true);

      try {
        const response = await apiClient.get(
          `communication/threads/${threadId}/messages/`,
          {
            params: {
              page,
              page_size: resolvedPageSize,
            },
          }
        );

        if (activeThreadRef.current !== threadId) {
          return null;
        }

        const dataResults = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];

        const normalizedResults = dataResults.map((message) => ({
          ...message,
          replies: Array.isArray(message.replies) ? message.replies : [],
        }));

        const previousIds = pageMessagesRef.current.get(page) || [];

        const loadedPagesWithoutCurrent = loadedPagesRef.current.filter(
          (loadedPage) => loadedPage !== page
        );
        const earliestLoadedPage =
          loadedPagesWithoutCurrent.length > 0
            ? Math.min(...loadedPagesWithoutCurrent)
            : null;
        const latestLoadedPage =
          loadedPagesWithoutCurrent.length > 0
            ? Math.max(...loadedPagesWithoutCurrent)
            : null;

        setAllMessages((prevMessages) => {
          let baseMessages = replace ? [] : [...prevMessages];

          if (!replace && previousIds.length > 0) {
            const idsToRemove = new Set(previousIds);
            baseMessages = baseMessages.filter(
              (message) => !idsToRemove.has(String(message.id))
            );
          }

          if (
            replace ||
            loadedPagesWithoutCurrent.length === 0 ||
            earliestLoadedPage === null ||
            latestLoadedPage === null
          ) {
            return normalizedResults;
          }

          if (page < earliestLoadedPage) {
            return mergeOrderedMessageGroups(normalizedResults, baseMessages);
          }

          if (page > latestLoadedPage) {
            return mergeOrderedMessageGroups(baseMessages, normalizedResults);
          }

          return mergeOrderedMessageGroups(baseMessages, normalizedResults);
        });

        const normalizedIds = normalizedResults.map((message) =>
          String(message.id)
        );
        pageMessagesRef.current.set(page, normalizedIds);

        setLoadedPages((prev) => {
          const nextSet = replace ? new Set() : new Set(prev);
          nextSet.add(page);
          const nextPages = Array.from(nextSet).sort((a, b) => a - b);
          loadedPagesRef.current = nextPages;
          return nextPages;
        });

        const previousPageNumber = extractPageNumber(response.data?.previous);
        const nextPageNumber = extractPageNumber(response.data?.next);

        setPageNavigation((prev) => {
          const nextNavigation = replace ? {} : { ...prev };
          nextNavigation[page] = {
            previous: previousPageNumber,
            next: nextPageNumber,
          };
          pageNavigationRef.current = nextNavigation;
          return nextNavigation;
        });

        return {
          previousPage: previousPageNumber,
          nextPage: nextPageNumber,
        };
      } catch (error) {
        console.error("Error fetching messages page", page, error);
        if (replace) {
          setAllMessages([]);
          setPageNavigation({});
          setLoadedPages([]);
          pageNavigationRef.current = {};
        }
        throw error;
      } finally {
        loadingPagesRef.current.delete(page);
        if (loadingPagesRef.current.size === 0) {
          setLoadingPage(false);
        }
      }
    },
    [
      mainThreadId,
      interceptorReady,
      mergeOrderedMessageGroups,
      currentUser?.id,
    ]
  );

  useEffect(() => {
    activeThreadRef.current = mainThreadId;
  }, [mainThreadId]);

  useEffect(() => {
    if (!mainThreadId) {
      resetPagingState();
      return;
    }

    if (!interceptorReady) return;
    if (isMessageThreadView) return;

    const targetPage = initialPage || 1;
    resetPagingState();

    loadPage(targetPage, { replace: true }).catch((error) => {
      console.error("Error loading initial messages page", error);
    });
  }, [
    mainThreadId,
    initialPage,
    conversationsRefreshKey,
    messagesRefreshKey,
    interceptorReady,
    isMessageThreadView,
    resetPagingState,
    loadPage,
  ]);

  useEffect(() => {
    if (highlightMessageId) {
      setPendingHighlightId(String(highlightMessageId));
    } else {
      setPendingHighlightId(null);
      setHighlightedMessageId(null);
    }
  }, [highlightMessageId, highlightMessagePosition]);

  useEffect(() => {
    if (!isMessageThreadView || !messageThreadId || !interceptorReady) {
      activeMessageThreadRequestRef.current = null;
      setIsLoadingMessageThread(false);
      return;
    }

    const requestKey = `${mainThreadId ?? "no-thread"}:${String(messageThreadId)}`;
    activeMessageThreadRequestRef.current = requestKey;

    resetPagingState();
    setIsLoadingMessageThread(true);
    const normalizeMessage = (message) =>
      message
        ? {
            ...message,
            replies: Array.isArray(message.replies) ? message.replies : [],
          }
        : null;

    const loadSynchronizedMessage = async () => {
      try {
        const response = await apiClient.get(`communication/messages/${messageThreadId}/`);
        if (activeMessageThreadRequestRef.current !== requestKey) {
          return;
        }
        const messageData = normalizeMessage(response?.data);
        if (messageData?.id) {
          setAllMessages([messageData]);
          return;
        }
      } catch (error) {
        console.warn(
          "Error loading synchronized message directly, trying thread fallback:",
          error
        );
      }

      if (!mainThreadId) {
        throw new Error("No main thread id available for fallback");
      }

      const fallbackResponse = await apiClient.get(
        `communication/threads/${mainThreadId}/messages/all/`
      );
      if (activeMessageThreadRequestRef.current !== requestKey) {
        return;
      }
      const fallbackMessages = Array.isArray(fallbackResponse?.data)
        ? fallbackResponse.data
        : [];
      const foundMessage = fallbackMessages.find(
        (message) => String(message?.id) === String(messageThreadId)
      );
      const normalizedFoundMessage = normalizeMessage(foundMessage);

      if (!normalizedFoundMessage?.id) {
        throw new Error("Synchronized message not found in fallback list");
      }

      setAllMessages([normalizedFoundMessage]);
    };

    loadSynchronizedMessage()
      .catch((error) => {
        if (activeMessageThreadRequestRef.current !== requestKey) {
          return;
        }
        console.error("Error loading synchronized message thread:", error);
        if (onShowNotification) {
          onShowNotification({
            message:
              "No se pudo cargar el mensaje sincronizado. Intenta nuevamente.",
            isError: true,
          });
        }
      })
      .finally(() => {
        if (activeMessageThreadRequestRef.current === requestKey) {
          setIsLoadingMessageThread(false);
        }
      });

    return () => {
      if (activeMessageThreadRequestRef.current === requestKey) {
        activeMessageThreadRequestRef.current = null;
      }
    };
  }, [
    interceptorReady,
    isMessageThreadView,
    mainThreadId,
    messageThreadId,
    onShowNotification,
    resetPagingState,
  ]);

  useEffect(() => {
    if (!pendingHighlightId) return;
    const targetId = String(pendingHighlightId);
    const element = messageRefs.current.get(targetId);
    const container = scrollContainerRef.current;
    if (!element || !container) {
      return;
    }

    const elementRect = element.getBoundingClientRect();
    if (isMobile) {
      window.scrollTo({
        top: Math.max(elementRect.top + window.scrollY - 120, 0),
        behavior: "smooth",
      });
    } else {
      const containerRect = container.getBoundingClientRect();
      const offset = elementRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({
        top: Math.max(offset - 120, 0),
        behavior: "smooth",
      });
    }

    setHighlightedMessageId(targetId);
    const timeout = setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === targetId ? null : current
      );
    }, 6000);

    setPendingHighlightId(null);
    if (onHighlightConsumed) {
      onHighlightConsumed();
    }

    return () => clearTimeout(timeout);
  }, [pendingHighlightId, allMessages, onHighlightConsumed, isMobile]);

  const invalidateClientThreads = useCallback(() => {
    if (internal) {
      queryClient.invalidateQueries({ queryKey: ["getData", INTERNAL_THREADS_ENDPOINT] });
      return;
    }
    if (!selectedClientId) return;

    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        if (!Array.isArray(queryKey)) return false;
        const [key, endpoint] = queryKey;
        if (key !== "getData" || typeof endpoint !== "string") return false;
        return endpoint.startsWith(
          `communication/threads/?client=${selectedClientId}`
        );
      },
    });
  }, [internal, queryClient, selectedClientId]);

  const closeArchiveEventModal = useCallback(() => {
    archiveEventRequestRef.current += 1;
    setArchiveEventModalOpen(false);
    setArchiveEventTargetMessage(null);
    setArchiveEventRecommendations([]);
    setSelectedArchiveEventIds([]);
    setIsArchivingEventMessages(false);
  }, []);

  const upsertMessage = useCallback((message, { prepend = true } = {}) => {
    if (!message) return;

    const normalizedMessage = {
      ...message,
      replies: Array.isArray(message.replies) ? message.replies : [],
    };

    setAllMessages((prevMessages) => {
      const existingIndex = prevMessages.findIndex(
        (msg) => String(msg.id) === String(normalizedMessage.id)
      );

      if (existingIndex !== -1) {
        const updatedMessages = [...prevMessages];
        updatedMessages[existingIndex] = {
          ...updatedMessages[existingIndex],
          ...normalizedMessage,
        };
        return updatedMessages;
      }

      return prepend
        ? [normalizedMessage, ...prevMessages]
        : [...prevMessages, normalizedMessage];
    });
  }, []);

  const appendCommentToMessage = useCallback((parentMessageId, comment) => {
    if (!parentMessageId || !comment) return;

    setAllMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (String(msg.id) !== String(parentMessageId)) {
          return msg;
        }

        const existingReplies = Array.isArray(msg.replies) ? msg.replies : [];
        const commentExists = existingReplies.some(
          (reply) => String(reply.id) === String(comment.id)
        );

        const updatedReplies = commentExists
          ? existingReplies.map((reply) =>
            String(reply.id) === String(comment.id) ? comment : reply
          )
          : [...existingReplies, comment];

        return {
          ...msg,
          replies: updatedReplies,
        };
      })
    );
  }, []);

  const removeAttachmentLocally = useCallback(
    ({ messageId, attachmentId, parentId }) => {
      if (!attachmentId) return;

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) => {
          const messageIdStr = String(messageId);
          const parentIdStr = parentId ? String(parentId) : null;
          const currentIdStr = String(msg.id);

          if (currentIdStr === messageIdStr) {
            return {
              ...msg,
              attachments: (msg.attachments || []).filter(
                (attachment) => String(attachment.id) !== String(attachmentId)
              ),
            };
          }

          if (parentIdStr && currentIdStr === parentIdStr) {
            const updatedReplies = (msg.replies || []).map((reply) => {
              if (String(reply.id) !== messageIdStr) {
                return reply;
              }

              return {
                ...reply,
                attachments: (reply.attachments || []).filter(
                  (attachment) => String(attachment.id) !== String(attachmentId)
                ),
              };
            });

            return {
              ...msg,
              replies: updatedReplies,
            };
          }

          return msg;
        })
      );
    },
    []
  );

  // Función para registrar ref de mensaje
  const setMessageRef = useCallback((messageId, element) => {
    const msgId = String(messageId);

    if (element) {
      messageRefs.current.set(msgId, element);

    } else {
      messageRefs.current.delete(msgId);
    }
  }, []);

  const earliestPage = loadedPages.length > 0 ? loadedPages[0] : null;
  const latestPage =
    loadedPages.length > 0 ? loadedPages[loadedPages.length - 1] : null;

  const previousPageToLoad =
    earliestPage !== null
      ? pageNavigation[earliestPage]?.previous ?? null
      : null;
  const nextPageToLoad =
    latestPage !== null ? pageNavigation[latestPage]?.next ?? null : null;

  const hasPreviousPage = Boolean(previousPageToLoad);
  const hasNextPage = Boolean(nextPageToLoad);
  const visibleMessages = useMemo(() => {
    if (!isMessageThreadView || !messageThreadId) {
      return allMessages;
    }
    return allMessages.filter(
      (message) => String(message.id) === String(messageThreadId)
    );
  }, [allMessages, isMessageThreadView, messageThreadId]);

  const getCurrentViewportPage = useCallback(() => {
    if (isMessageThreadView) {
      return initialPage || 1;
    }

    const loadedPageList = loadedPagesRef.current;
    if (!loadedPageList.length) {
      return initialPage || 1;
    }

    const containerElement = scrollContainerRef.current;
    if (!containerElement) {
      return loadedPageList[0];
    }

    // En móvil el contenedor puede quedar oculto bajo la cabecera fija
    const visibleTop = Math.max(
      containerElement.getBoundingClientRect().top,
      DOCKED_HEADER_HEIGHT
    );
    let bestMatch = {
      page: loadedPageList[0],
      distance: Number.POSITIVE_INFINITY,
    };

    loadedPageList.forEach((page) => {
      const pageMessageIds = pageMessagesRef.current.get(page) || [];

      pageMessageIds.forEach((messageId) => {
        const element = messageRefs.current.get(String(messageId));
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top - visibleTop);

        if (distance < bestMatch.distance) {
          bestMatch = { page, distance };
        }
      });
    });

    return bestMatch.page || initialPage || 1;
  }, [initialPage, isMessageThreadView]);

  const refreshCurrentViewportPage = useCallback(
    async () => {
      if (!mainThreadId || !interceptorReady || isMessageThreadView) {
        setMessagesRefreshKey((prev) => prev + 1);
        return;
      }

      const currentPage = Math.max(1, Number(getCurrentViewportPage()) || initialPage || 1);

      await loadPage(currentPage, {
        replace: true,
      });
    },
    [
      getCurrentViewportPage,
      initialPage,
      interceptorReady,
      isMessageThreadView,
      loadPage,
      mainThreadId,
    ]
  );

  const handlePreviousPage = useCallback(() => {
    if (!previousPageToLoad || previousPageToLoad < 1) return;
    if (loadedPages.includes(previousPageToLoad)) return;
    loadPage(previousPageToLoad);
  }, [previousPageToLoad, loadedPages, loadPage]);

  const handleNextPage = useCallback(() => {
    if (!nextPageToLoad || nextPageToLoad < 1) return;
    if (loadedPages.includes(nextPageToLoad)) return;
    loadPage(nextPageToLoad);
  }, [nextPageToLoad, loadedPages, loadPage]);

  // Función para mapear mensajes del backend a la estructura esperada por ThreadPost
  const mapMessageToPost = (message) => {
    const mappedComments = (message.replies || []).map((reply) => ({
      id: reply.id,
      authorId: reply.sender?.id || "unknown",
      author: reply.sender?.full_name || reply.author_display_name || "Usuario",
      authorRole: reply.sender?.role || "user", // NUEVO: rol del autor del comentario
      authorProfileImage: getAvatarImageUrl(reply.sender?.profile_image), // NUEVO: imagen de perfil del autor del comentario
      content: reply.content,
      timestamp: reply.created_at,
      attachments: reply.attachments || [], // NUEVO: adjuntos del comentario
      is_liked: reply.is_liked || false,
      like_count: reply.like_count || 0,
      liked_by: reply.liked_by || [],
    }));

    return {
      id: message.id,
      authorId: message.sender?.id || "system",
      content: message.content,
      timestamp: message.created_at,
      attachments: message.attachments || [],
      tags: [], // Los tags no están en la estructura del backend por ahora
      comments: mappedComments,
      // Información del autor principal
      author: message.sender?.full_name || message.author_display_name || "Sistema",
      authorRole: message.sender?.role || "system",
      authorProfileImage: getAvatarImageUrl(message.sender?.profile_image), // NUEVO: imagen de perfil del autor
      // Campos adicionales del backend
      messageType: message.message_type, // "event", "normal", "action", "important"
      isThread: !!message.is_thread,
      isThreadTitle: message.is_thread_title || null,
      isResolved: message.is_resolved,
      actionAssignedTo: message.action_assigned_to,
      taskId: message.task_id,
      liked: message.is_liked || false, // Si el usuario actual ha dado "me gusta"
      likeCount: message.like_count || 0, // Contador de "me gusta"
      likedBy: message.liked_by || [],
      isRead: message.is_read || false, // Estado de lectura del mensaje
    };
  };

  // Helper para verificar si el usuario actual es participante del thread
  const isUserParticipant = () => {
    if (!currentUser) return false;

    // Los superadmins pueden acceder a todas las conversaciones
    if (currentUser.role === "superadmin") return true;

    // Para otros usuarios, verificar si están en la lista de participantes
    if (!clientData?.participants) return false;

    return clientData.participants.some(
      (participant) =>
        participant.id === currentUser.id ||
        participant.user_id === currentUser.id
    );
  };

  // Funciones para manejar acciones
  const handleNewPost = async (
    content,
    messageType,
    consultantId = null,
    attachments = []
  ) => {
    if ((!selectedClientId && !internal) || !mainThreadId) return;
    if (isMessageThreadView) {
      throw new Error(
        "Este hilo está sincronizado a un mensaje. Solo puedes participar desde los comentarios."
      );
    }

    // Validar si el usuario es participante del thread
    if (!isUserParticipant()) {
      throw new Error(
        "No tienes permisos para participar en esta conversación. Solo los participantes asignados pueden enviar mensajes."
      );
    }

    try {
      let response;
      if (attachments.length > 0) {
        // Usar la función de envío con adjuntos
        response = await sendMessageWithAttachments(
          mainThreadId,
          content,
          attachments,
          messageType,
          consultantId
        );
      } else {
        // Envío tradicional sin adjuntos
        const newPost = {
          thread: mainThreadId,
          content: content,
          message_type: messageType,
        };

        // Si es un mensaje de acción y se seleccionó un consultor, agregarlo
        if (messageType === "action" && consultantId) {
          newPost.action_assigned_to = consultantId;
        }

        response = await apiClient.post("communication/messages/", newPost);
      }

      const createdMessage = response?.data ?? response;

      if (createdMessage?.id) {
        upsertMessage(createdMessage);
      }

      if (onMessageSent) {
        onMessageSent({ threadId: mainThreadId, clientId: selectedClientId });
      }

      // Incrementar refresh key para forzar recarga de mensajes
      setMessagesRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error al enviar el post:", error);

      // Manejo específico de errores del backend
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            throw new Error(
              data.detail ||
              "Los datos enviados no son válidos. Verifica el contenido del mensaje."
            );
          case 401:
            throw new Error(
              "Tu sesión ha expirado. Por favor, inicia sesión nuevamente."
            );
          case 403:
            throw new Error(
              "No tienes permisos para participar en esta conversación. Solo los participantes asignados pueden enviar mensajes."
            );
          case 404:
            throw new Error(
              "La conversación no fue encontrada o ya no existe."
            );
          case 413:
            throw new Error(
              "El mensaje o los archivos adjuntos son demasiado grandes."
            );
          default:
            throw new Error(
              "Error del servidor. Intenta nuevamente en unos momentos."
            );
        }
      }

      // Error de conexión o desconocido
      throw new Error(
        "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
      );
    }
  };

  const handleCommentPost = async (postId, comment, attachments = []) => {
    if (!mainThreadId || (!comment.trim() && attachments.length === 0)) return;

    // Validar si el usuario es participante del thread
    if (!isUserParticipant()) {
      throw new Error(
        "No tienes permisos para participar en esta conversación. Solo los participantes asignados pueden enviar mensajes."
      );
    }

    try {
      let response;
      if (attachments.length > 0) {
        // Usar la función de envío con adjuntos para comentarios
        const formData = new FormData();
        formData.append("thread", mainThreadId);
        formData.append("content", comment);
        formData.append("parent", postId); // ID del mensaje padre

        // Añadir archivos adjuntos
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });

        response = await apiClient.post("/communication/messages/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Envío tradicional sin adjuntos
        const newComment = {
          thread: mainThreadId,
          parent: postId, // ID del mensaje padre
          content: comment,
        };

        response = await apiClient.post("communication/messages/", newComment);
      }

      const createdComment = response?.data ?? response;

      if (createdComment?.id) {
        const parentMessageId = createdComment.parent || postId;

        appendCommentToMessage(parentMessageId, {
          ...createdComment,
          replies: Array.isArray(createdComment.replies)
            ? createdComment.replies
            : [],
        });
      }


    } catch (error) {
      console.error("Error al enviar el comentario:", error);

      // Manejo específico de errores del backend
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            throw new Error(
              data.detail ||
              "Los datos del comentario no son válidos. Verifica el contenido."
            );
          case 401:
            throw new Error(
              "Tu sesión ha expirado. Por favor, inicia sesión nuevamente."
            );
          case 403:
            throw new Error(
              "No tienes permisos para participar en esta conversación. Solo los participantes asignados pueden enviar mensajes."
            );
          case 404:
            throw new Error("El mensaje o la conversación no fue encontrada.");
          case 413:
            throw new Error(
              "El comentario o los archivos adjuntos son demasiado grandes."
            );
          default:
            throw new Error(
              "Error del servidor. Intenta nuevamente en unos momentos."
            );
        }
      }

      // Error de conexión o desconocido
      throw new Error(
        "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
      );
    }
  };

  // Función para actualizar el tipo de mensaje
  const handleUpdateMessageType = async (
    messageId,
    newType,
    assignedTo = null
  ) => {
    // Encontrar el mensaje actual
    const currentMessage = allMessages.find((msg) => msg.id === messageId);
    if (!currentMessage) return;

    // Estado anterior para rollback en caso de error
    const previousMessageType = currentMessage.message_type;
    const previousActionAssignedTo = currentMessage.action_assigned_to;

    // Actualización optimista: cambiar el estado local inmediatamente
    let optimisticUpdate = {};

    if (newType === "action" && assignedTo) {
      // Cuando asignas a alguien, actualizar tanto el tipo como el asignado
      optimisticUpdate.message_type = "action";
      optimisticUpdate.action_assigned_to = assignedTo.id;
    } else {
      // Para otros tipos (normal, important), actualizar el message_type
      optimisticUpdate.message_type = newType;
      // Si no es action, limpiar el asignado
      if (newType !== "action") {
        optimisticUpdate.action_assigned_to = null;
      }
    }

    // Actualizar estado local
    setAllMessages((prevMessages) =>
      prevMessages.map((msg) =>
        String(msg.id) === String(messageId)
          ? { ...msg, ...optimisticUpdate }
          : msg
      )
    );

    try {

      let updateData = {};

      // Si es tipo action, solo actualizar el consultor asignado
      if (newType === "action" && assignedTo) {
        updateData.action_assigned_to = assignedTo.id;
      } else {
        // Para otros tipos (normal, important), actualizar el message_type
        updateData.message_type = newType;
      }

      const response = await apiClient.patch(
        `communication/messages/${messageId}/`,
        updateData
      );

      if (response?.data) {
        upsertMessage(response.data, { prepend: false });
      }

      // Éxito: la actualización local ya está aplicada
    } catch (error) {
      console.error("Error al actualizar mensaje:", error);

      // Error: revertir cambios locales y refrescar desde backend
      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? {
              ...msg,
              message_type: previousMessageType,
              action_assigned_to: previousActionAssignedTo,
            }
            : msg
        )
      );
    }
  }; // Función para resolver mensaje
  const handleResolveMessage = async (messageId) => {
    try {
      await apiClient.post(`communication/messages/${messageId}/resolve/`);

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? { ...msg, is_resolved: true }
            : msg
        )
      );
    } catch (error) {
      console.error("Error al resolver mensaje:", error);
    }
  };

  // Función para desresolver mensaje
  const handleUnresolveMessage = async (messageId) => {
    try {
      await apiClient.post(`communication/messages/${messageId}/unresolve/`);

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? { ...msg, is_resolved: false }
            : msg
        )
      );
    } catch (error) {
      console.error("Error al desresolver mensaje:", error);
    }
  };

  // Función para manejar likes en posts o comentarios con actualización optimista
  const handleLikeMessage = async (messageId) => {
    let targetMessage = null;
    let isComment = false;
    let parentPostId = null;

    // Buscar en mensajes raíz
    targetMessage = allMessages.find((msg) => String(msg.id) === String(messageId));

    if (!targetMessage) {
      // Buscar en comentarios de todos los mensajes
      for (const msg of allMessages) {
        const comment = (msg.replies || []).find(
          (reply) => String(reply.id) === String(messageId)
        );
        if (comment) {
          targetMessage = comment;
          isComment = true;
          parentPostId = msg.id;
          break;
        }
      }
    }

    if (!targetMessage) return;

    // Estado anterior para rollback en caso de error
    const previousLiked = targetMessage.is_liked;
    const previousLikeCount = targetMessage.like_count;
    const previousLikedBy = targetMessage.liked_by || [];

    // Actualización optimista
    const wasLiked = targetMessage.is_liked;
    const newLiked = !wasLiked;
    const newLikeCount = wasLiked
      ? Math.max(0, (targetMessage.like_count || 0) - 1)
      : (targetMessage.like_count || 0) + 1;
    const currentUserEntry = currentUser
      ? { full_name: currentUser.full_name || currentUser.name || "", profile_image: currentUser.profile_image || null }
      : null;
    const newLikedBy = wasLiked
      ? previousLikedBy.filter((u) => u.full_name !== currentUserEntry?.full_name)
      : currentUserEntry
      ? [...previousLikedBy, currentUserEntry]
      : previousLikedBy;

    // Actualizar estado local
    setAllMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (!isComment) {
          return String(msg.id) === String(messageId)
            ? { ...msg, is_liked: newLiked, like_count: newLikeCount, liked_by: newLikedBy }
            : msg;
        } else {
          if (String(msg.id) !== String(parentPostId)) return msg;

          const updatedReplies = (msg.replies || []).map((reply) =>
            String(reply.id) === String(messageId)
              ? { ...reply, is_liked: newLiked, like_count: newLikeCount, liked_by: newLikedBy }
              : reply
          );

          return { ...msg, replies: updatedReplies };
        }
      })
    );

    try {
      await apiClient.post(`communication/messages/${messageId}/like/`);
    } catch (error) {
      console.error("Error al dar like:", error);
      // Revertir en caso de error
      setAllMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (!isComment) {
            return String(msg.id) === String(messageId)
              ? { ...msg, is_liked: previousLiked, like_count: previousLikeCount, liked_by: previousLikedBy }
              : msg;
          } else {
            if (String(msg.id) !== String(parentPostId)) return msg;
            const updatedReplies = (msg.replies || []).map((reply) =>
              String(reply.id) === String(messageId)
                ? { ...reply, is_liked: previousLiked, like_count: previousLikeCount, liked_by: previousLikedBy }
                : reply
            );
            return { ...msg, replies: updatedReplies };
          }
        })
      );
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    if (!newContent || !newContent.trim()) {
      throw new Error("El contenido del mensaje no puede estar vacío.");
    }

    try {
      await apiClient.patch(`communication/messages/${messageId}/`, {
        content: newContent,
      });

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? { ...msg, content: newContent }
            : msg
        )
      );
    } catch (error) {
      console.error("Error al actualizar mensaje:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Error al actualizar el mensaje. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await apiClient.delete(`communication/messages/${messageId}/`);

      // Verificar códigos de éxito específicos
      if (response.status === 204 || response.status === 200) {
        setAllMessages((prevMessages) =>
          prevMessages.filter((msg) => String(msg.id) !== String(messageId))
        );
      } else {
        throw new Error(`Respuesta inesperada del servidor: ${response.status}`);
      }
    } catch (error) {
      console.error("Error al eliminar mensaje:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Error al eliminar el mensaje. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  };

  const handleEditComment = async ({ commentId, parentId, content }) => {
    if (!content || !content.trim()) {
      throw new Error("El contenido del comentario no puede estar vacío.");
    }

    try {
      await apiClient.patch(`communication/messages/${commentId}/`, {
        content,
      });

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (String(msg.id) !== String(parentId)) {
            return msg;
          }

          const updatedReplies = (msg.replies || []).map((reply) =>
            String(reply.id) === String(commentId)
              ? { ...reply, content }
              : reply
          );

          return {
            ...msg,
            replies: updatedReplies,
          };
        })
      );
    } catch (error) {
      console.error("Error al actualizar comentario:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Error al actualizar el comentario. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  };

  const handleDeleteComment = async ({ commentId, parentId }) => {
    try {
      const response = await apiClient.delete(`communication/messages/${commentId}/`);

      // Verificar códigos de éxito específicos
      if (response.status === 204 || response.status === 200) {
        setAllMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (String(msg.id) !== String(parentId)) {
              return msg;
            }

            const updatedReplies = (msg.replies || []).filter(
              (reply) => String(reply.id) !== String(commentId)
            );

            return {
              ...msg,
              replies: updatedReplies,
            };
          })
        );
      } else {
        throw new Error(`Respuesta inesperada del servidor: ${response.status}`);
      }
    } catch (error) {
      console.error("Error al eliminar comentario:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Error al eliminar el comentario. Intenta nuevamente.";
      throw new Error(errorMessage);
    }
  };

  // Función para refrescar datos después de eliminar attachment
  const handleDeleteAttachment = ({ messageId, attachmentId, parentId }) => {
    removeAttachmentLocally({ messageId, attachmentId, parentId });
  };

  const handleToggleArchiveEventSelection = useCallback((messageId) => {
    setSelectedArchiveEventIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId]
    );
  }, []);

  const handleOpenArchiveEventModal = useCallback(
    async (messageId) => {
      const targetMessage = allMessages.find(
        (message) => String(message.id) === String(messageId)
      );

      if (!targetMessage || !mainThreadId) {
        return;
      }

      const requestId = archiveEventRequestRef.current + 1;
      archiveEventRequestRef.current = requestId;

      try {
        const currentPage = getCurrentViewportPage();
        const response = await apiClient.get(
          `communication/threads/${mainThreadId}/messages/`,
          {
            params: {
              page: currentPage,
              page_size: MESSAGE_PAGE_SIZE,
            },
          }
        );

        if (archiveEventRequestRef.current !== requestId) {
          return;
        }

        const threadMessages = Array.isArray(response?.data?.results)
          ? response.data.results
          : [];
        const recommendations = threadMessages.filter(
          (message) =>
            String(message?.id) !== String(messageId) &&
            message?.message_type === "event"
        );

        if (archiveEventRequestRef.current !== requestId) {
          return;
        }

        setArchiveEventTargetMessage(targetMessage);
        setArchiveEventRecommendations(recommendations);
        setSelectedArchiveEventIds([]);
        setArchiveEventModalOpen(true);
      } catch (error) {
        console.error("Error loading archive event recommendations:", error);
        if (archiveEventRequestRef.current === requestId) {
          setArchiveEventTargetMessage(targetMessage);
          setArchiveEventRecommendations([]);
          setSelectedArchiveEventIds([]);
          setArchiveEventModalOpen(true);
        }
      }
    },
    [allMessages, getCurrentViewportPage, mainThreadId]
  );

  const handleConfirmArchiveEventMessages = useCallback(async () => {
    if (!archiveEventTargetMessage?.id || isArchivingEventMessages) return;

    const idsToArchive = [
      archiveEventTargetMessage.id,
      ...selectedArchiveEventIds,
    ];

    setIsArchivingEventMessages(true);

    try {
      await apiClient.post("communication/messages/archive/", {
        message_ids: idsToArchive,
      });

      invalidateClientThreads();

      try {
        await refreshCurrentViewportPage();
      } catch (refreshError) {
        console.error("Error refreshing thread after archiving event messages:", refreshError);
        setMessagesRefreshKey((prev) => prev + 1);
      }

      closeArchiveEventModal();

      if (onShowNotification) {
        onShowNotification({
          message:
            idsToArchive.length > 1
              ? "Mensajes archivados correctamente."
              : "Mensaje archivado correctamente.",
          isError: false,
        });
      }
    } catch (error) {
      console.error("Error archiving event messages:", error);
      const rawError =
        error?.response?.data?.detail ||
        error?.response?.data?.message_ids ||
        "No se pudieron archivar los mensajes seleccionados.";
      if (onShowNotification) {
        onShowNotification({
          message: Array.isArray(rawError) ? rawError.join(", ") : rawError,
          isError: true,
        });
      }
    } finally {
      setIsArchivingEventMessages(false);
    }
  }, [
    archiveEventTargetMessage,
    closeArchiveEventModal,
    invalidateClientThreads,
    isArchivingEventMessages,
    onShowNotification,
    refreshCurrentViewportPage,
    selectedArchiveEventIds,
  ]);

  const resolveAssigneeObject = useCallback(
    (assigneeId) => {
      if (!assigneeId && assigneeId !== 0) return null;
      const numericId = Number(assigneeId);
      if (Number.isNaN(numericId)) {
        return null;
      }
      const consultantPool =
        taskModalConsultants.length > 0
          ? taskModalConsultants
          : availableConsultants;
      return (
        consultantPool.find(
          (consultant) => Number(consultant.id) === numericId
        ) || null
      );
    },
    [availableConsultants, taskModalConsultants]
  );

  const handleOpenTaskModal = useCallback(
    (post, consultantsOverride = null) => {
      if (!post) return;
      const consultantList =
        Array.isArray(consultantsOverride) && consultantsOverride.length > 0
          ? consultantsOverride
          : availableConsultants;
      setTaskModalConsultants(consultantList);

      const initialAssigned =
        (typeof post.actionAssignedTo === "object" &&
          post.actionAssignedTo !== null
          ? post.actionAssignedTo.id
          : post.actionAssignedTo) || "";

      setTaskModalMessage(post);
      setTaskModalTask({
        id: post.taskId || null,
        title: "",
        description: "",
        assigned_to: initialAssigned ? String(initialAssigned) : "",
        deadline: null,
        documentation_urls: [],
      });
      setTaskModalOpen(true);

      if (post.taskId) {
        apiClient
          .get(`projects/tasks/${post.taskId}/`)
          .then((response) => {
            if (response?.data) {
              setTaskModalTask(response.data);
            }
          })
          .catch((error) => {
            console.error("Error al obtener tarea:", error);
            if (onShowNotification) {
              onShowNotification({
                message:
                  error?.response?.data?.detail ||
                  "No se pudo cargar la tarea del mensaje.",
                isError: true,
              });
            }
          });
      }
    },
    [availableConsultants, onShowNotification]
  );

  const handleCloseTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setTaskModalMessage(null);
    setTaskModalTask(null);
    setTaskModalConsultants([]);
  }, []);

  const handleMoveToNewThread = useCallback((messageId) => {
    if (!messageId) return;
    setMoveToNewThreadError("");
    setMoveToNewThreadTitle("");
    setMoveToNewThreadMessageId(messageId);
    setMoveToNewThreadModalOpen(true);
  }, []);

  const handleConfirmMoveToNewThread = useCallback(async () => {
    const messageId = moveToNewThreadMessageId;
    if (!messageId || isMovingToNewThread) return;

    const title = (moveToNewThreadTitle || "").trim();
    if (!title) {
      setMoveToNewThreadError("Introduce un título para el nuevo hilo.");
      return;
    }

    setIsMovingToNewThread(true);
    setMoveToNewThreadError("");

    try {
      const response = await apiClient.post(
        `communication/messages/${messageId}/move_to_new_thread/`,
        { title }
      );

      const newThread = response?.data || null;
      const newThreadId = newThread?.id || null;
      const targetClientId = newThread?.client?.id || selectedClientId || null;

      if (internal) {
        invalidateClientThreads();
      } else if (targetClientId) {
        const endpoint = `communication/threads/?client=${targetClientId}`;
        queryClient.invalidateQueries({ queryKey: ["getData", endpoint] });
      }

      // Refresh current thread messages (so the moved message disappears if user stays).
      setMessagesRefreshKey((prev) => prev + 1);

      setMoveToNewThreadModalOpen(false);
      setMoveToNewThreadMessageId(null);
      setMoveToNewThreadTitle("");

      if (internal && newThreadId) {
        navigate(
          buildInternalThreadUrl(newThread.thread_id, {
            messageThreadId: newThreadId,
            messageThreadTitle: newThread.title || title,
          }),
          { state: { toast: { message: "Mensaje movido a un nuevo hilo.", isError: false } } }
        );
      } else if (targetClientId && newThreadId) {
        navigate(
          `/conversations/${targetClientId}/${newThreadId}?t=${Date.now()}`,
          {
            state: {
              toast: { message: "Mensaje movido a un nuevo hilo.", isError: false },
            },
          }
        );
      } else if (onShowNotification) {
        onShowNotification({
          message: "Hilo creado, pero no se pudo navegar automáticamente.",
          isError: true,
        });
      }
    } catch (error) {
      console.error("Error moving message to new thread:", error);
      const data = error?.response?.data || null;
      const fieldTitleError = Array.isArray(data?.title)
        ? data.title[0]
        : data?.title;
      const errorMessage =
        data?.detail ||
        fieldTitleError ||
        "No se pudo mover el mensaje a un nuevo hilo.";
      setMoveToNewThreadError(errorMessage);
      if (onShowNotification) {
        onShowNotification({ message: errorMessage, isError: true });
      }
    } finally {
      setIsMovingToNewThread(false);
    }
  }, [
    internal,
    invalidateClientThreads,
    isMovingToNewThread,
    moveToNewThreadMessageId,
    moveToNewThreadTitle,
    navigate,
    onShowNotification,
    queryClient,
    selectedClientId,
  ]);

  const handleConversationTaskSubmit = useCallback(
    async ({ task, values }) => {
      if (!taskModalMessage) {
        throw new Error("No hay mensaje seleccionado.");
      }

      const messageId = taskModalMessage.id;
      const assignedToId = values.assigned_to;

      if (!assignedToId) {
        throw new Error("Debes seleccionar un responsable para la tarea.");
      }

      const extractTaskId = (data) => {
        if (!data) return null;
        if (typeof data.task_id !== "undefined" && data.task_id !== null) {
          return data.task_id;
        }
        if (data.task && typeof data.task === "object") {
          return data.task.id ?? null;
        }
        return null;
      };

      let currentTaskId =
        task?.id || taskModalMessage.taskId || taskModalTask?.id || null;

      if (!currentTaskId) {
        const response = await apiClient.patch(
          `communication/messages/${messageId}/`,
          {
            message_type: "action",
            action_assigned_to: assignedToId,
          }
        );

        if (response?.data) {
          currentTaskId = extractTaskId(response.data);
          // No llamamos a upsertMessage aquí porque la actualización local se hace más abajo (líneas 1138-1149)
          // Esto previene que aparezca un mensaje duplicado de "Sistema"
        }
      }

      let taskResponse = null;
      if (currentTaskId) {
        taskResponse = await apiClient.patch(
          `projects/tasks/${currentTaskId}/`,
          values
        );

        if (taskResponse?.data) {
          setTaskModalTask(taskResponse.data);
        }
      } else {
        console.warn(
          `No se pudo obtener el ID de tarea para el mensaje ${messageId}. Se actualiza solo el mensaje.`
        );
      }

      const assignedUser = resolveAssigneeObject(assignedToId);

      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          String(msg.id) === String(messageId)
            ? {
              ...msg,
              message_type: "action",
              action_assigned_to: assignedUser || assignedToId,
              task_id: currentTaskId || null,
            }
            : msg
        )
      );

      setTaskModalMessage((prev) =>
        prev
          ? {
            ...prev,
            messageType: "action",
            actionAssignedTo: assignedUser || assignedToId,
            taskId: currentTaskId || null,
          }
          : prev
      );

      if (onShowNotification) {
        onShowNotification({
          message: "Tarea actualizada correctamente",
          isError: false,
        });
      }

      handleCloseTaskModal();
    },
    [
      taskModalMessage,
      taskModalTask,
      resolveAssigneeObject,
      onShowNotification,
      upsertMessage,
      setAllMessages,
      handleCloseTaskModal,
    ]
  );

  // Vista cuando no hay cliente seleccionado (verificar ANTES de permisos)
  if (!selectedClientId && !internal) {
    // Determinar mensaje según el rol del usuario
    const isClientRole = currentUser?.role === "client";
    const emptyMessage = isClientRole
      ? "Selecciona una empresa para acceder a sus conversaciones"
      : "Selecciona un cliente para conversar";

    return (
      <Box
        sx={{
          height: containerHeight,
          minHeight: containerMinHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EmptyStateMessage
          icon={MessageSquare}
          iconSize={48}
          message={emptyMessage}
        />
      </Box>
    );
  }

  // Si no es participante, mostrar panel blanco con mensaje de error
  if (!isUserParticipant() && !isMessageThreadView) {
    return (
      <Box
        sx={{
          height: containerHeight,
          minHeight: containerMinHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
      >
        <EmptyStateMessage
          icon={MessageSquare}
          iconSize={48}
          message="No tienes permisos para participar en esta conversación"
        />
      </Box>
    );
  }

  // Vista cuando no existe thread para el cliente
  if (!mainThreadId) {
    return (
      <Box
        sx={{
          height: containerHeight,
          minHeight: containerMinHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
      >
        <EmptyStateMessage
          icon={MessageSquare}
          iconSize={48}
          message="Inicia la primera conversación"
        />
      </Box>
    );
  }

  return (
    <ClientReadOnlyScope readOnly={isFreePlanReadOnly}>
      <Box
        sx={{
          height: containerHeight,
          display: "flex",
          flexDirection: "column",
          overflow: { xs: "clip", md: "hidden" },
          // Chrome recoloca el scroll al cargar páginas y Safari no: mismo comportamiento en ambos
          overflowAnchor: "none",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Contenido del thread con scroll interno */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: { xs: "visible", md: "auto" },
            padding: 0,
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            // Estilos discretos para el scrollbar
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: theme.palette.text.secondary,
              },
            },
            // Para Firefox
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.divider} transparent`,
          }}
        >
          {/* Composer para nuevo post */}
          <Box p={"18px 18px 0px 18px"}>
            <ThreadComposer
              onPost={handleNewPost}
              clientData={clientData}
              currentUser={currentUser}
              disabled={isFreePlanReadOnly || !isUserParticipant() || isMessageThreadView}
              hideTextInput={isMessageThreadView}
              disabledPlaceholder={
                isFreePlanReadOnly
                  ? "Cliente en solo lectura"
                  : "No puedes participar en esta conversación"
              }
              disabledMessage={
                isFreePlanReadOnly
                  ? null
                  : isMessageThreadView
                  ? `Este hilo está sincronizado a un solo mensaje, por lo que no puedes escribir mensajes nuevos. Para participar, usa los comentarios.`
                  : !isUserParticipant()
                  ? "No tienes permisos para participar en esta conversación. Solo los participantes asignados pueden enviar mensajes."
                  : null
              }
              conversationFontSize={user?.ui_preferences?.conversation_font_size}
            />
          </Box>

          {hasPreviousPage && !isMessageThreadView && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "12px 18px 0 18px",
              }}
            >
              <Button
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={loadingPage}
                startIcon={
                  loadingPage ? <CircularProgress size={14} color="inherit" /> : <ChevronUp size={16} />
                }
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&:disabled": {
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.disabled,
                  },
                }}
              >
                Página anterior
              </Button>
            </Box>
          )}

          {/* Lista de mensajes */}
          <Box>
            {(loadingPage || isLoadingMessageThread) && visibleMessages.length === 0 && (
              <Box
                sx={{
                  padding: "48px 32px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {visibleMessages?.map((message) => (
              <ThreadPost
                key={message.id}
                post={mapMessageToPost(message)}
                onComment={handleCommentPost}
                onUpdateMessageType={handleUpdateMessageType}
                onResolveMessage={handleResolveMessage}
                onUnresolveMessage={handleUnresolveMessage}
                onDeleteAttachment={handleDeleteAttachment}
                onLike={handleLikeMessage}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                onEditComment={handleEditComment}
                onDeleteComment={handleDeleteComment}
                onShowNotification={onShowNotification}
                onAssignTask={handleOpenTaskModal}
                onMoveToNewThread={handleMoveToNewThread}
                onArchiveEvent={handleOpenArchiveEventModal}
                consultants={availableConsultants}
                currentUser={currentUser}
                participants={participants}
                setMessageRef={setMessageRef}
                isHighlighted={String(message.id) === highlightedMessageId}
                conversationFontSize={user?.ui_preferences?.conversation_font_size}
                readOnly={isFreePlanReadOnly}
              />
            ))}

            {(!visibleMessages || visibleMessages.length === 0) &&
              !loadingPage &&
              !isLoadingMessageThread && (
              <Box sx={{ padding: "64px 32px" }}>
                <EmptyStateMessage
                  icon={MessageSquare}
                  message={
                    isMessageThreadView
                      ? "No se encontró el mensaje sincronizado."
                      : "No hay actividad reciente"
                  }
                />
              </Box>
            )}

            {hasNextPage && !isMessageThreadView && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 3,
                  mb: 2,
                  px: "18px",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleNextPage}
                  disabled={loadingPage}
                  startIcon={
                    loadingPage ? <CircularProgress size={14} color="inherit" /> : <ChevronDown size={16} />
                  }
                  sx={{
                    borderRadius: "20px",
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&:disabled": {
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.disabled,
                    },
                  }}
                >
                  Página siguiente
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        <EditTaskModal
          open={taskModalOpen}
          onClose={handleCloseTaskModal}
          task={
            taskModalTask || {
              id: null,
              title: "",
              description: "",
              assigned_to: "",
              deadline: null,
              documentation_urls: [],
            }
          }
          consultants={
            taskModalConsultants.length > 0
              ? taskModalConsultants
              : availableConsultants
          }
          hideTaskInfo
          onSubmitOverride={handleConversationTaskSubmit}
          onErrorPopup={
            onShowNotification
              ? (message) => onShowNotification({ message, isError: true })
              : undefined
          }
        />

        <ArchiveEventMessagesModal
          open={archiveEventModalOpen}
          onClose={() => {
            if (isArchivingEventMessages) return;
            closeArchiveEventModal();
          }}
          clientName={clientData?.name || archiveEventTargetMessage?.client_name || ""}
          targetMessage={archiveEventTargetMessage}
          recommendations={archiveEventRecommendations}
          selectedIds={selectedArchiveEventIds}
          onToggleSelection={handleToggleArchiveEventSelection}
          error=""
          onConfirm={handleConfirmArchiveEventMessages}
          isSubmitting={isArchivingEventMessages}
        />

        <ConfirmModal
          open={moveToNewThreadModalOpen}
          onClose={() => {
            setMoveToNewThreadModalOpen(false);
            setMoveToNewThreadMessageId(null);
            setMoveToNewThreadError("");
            setMoveToNewThreadTitle("");
          }}
          onConfirm={handleConfirmMoveToNewThread}
          title="Mover a nuevo hilo"
          question="¿Mover este mensaje a un nuevo hilo?"
          description="Se moverá este mensaje y todos sus comentarios al nuevo hilo. El mensaje desaparecerá de este hilo y se mostrará en el nuevo hilo."
          confirmLabel="Mover a nuevo hilo"
          loadingLabel="Moviendo..."
          loading={isMovingToNewThread}
          confirmDisabled={!moveToNewThreadTitle.trim()}
        >
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Título del hilo
            </Typography>
            <CustomTextField
              placeholder="Introduce un título"
              value={moveToNewThreadTitle}
              onChange={(e) => setMoveToNewThreadTitle(e.target.value)}
              error={!!moveToNewThreadError && !moveToNewThreadTitle.trim()}
            />
          </Box>
          {moveToNewThreadError && (
            <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
              {moveToNewThreadError}
            </Typography>
          )}
        </ConfirmModal>
      </Box>
    </ClientReadOnlyScope>
  );
}
